import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Avatar,
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import SearchIcon from "@mui/icons-material/Search";
import * as adminSubscriptionServices from "../../../contexts/services/admin/adminSubscriptionServices";
import SecurityGuard from "../../guards/SecurityGuard";
import { useGuardRequest } from "../../context/useGuardRequest";
import { formatUserType } from "../../utils";
import ProcessingDialog from "../../library/ProcessingDialog";
import Dialog from "../../library/Dialog";

import SubscriptionPricingPanel from "./SubscriptionPricingPanel";
import { subscriptionCardSx } from "./subscriptionStyles";
import {
  formatMoney,
  parseDiscountInput,
  effectiveDiscount,
} from "./subscriptionPricing";
import {
  trialDate,
  shiftTrialDate,
  trialDateInput,
  parseTrialDateInput,
  trialDaysBetween,
  formatTrialDate,
  maxTrialReductionDays,
} from "./subscriptionTrialDates";
import SubscriptionEmptyView from "./SubscriptionEmptyView";
import SubscriptionLoadingView from "./SubscriptionLoadingView";

const formatDate = (d) => (d ? new Date(d).toLocaleString() : null);

const getMessage = (c, currency) => {
  const start = formatDate(c.effective_date);
  const end = formatDate(c.effective_date_end);
  const fee = formatMoney(c.charge_after_discount, currency);

  switch (c.change_type) {
    case "add_user":
      return `Added a user${c.status === "trial" ? " (trial)" : ""}${
        fee ? ` for ${fee}` : ""
      }${start && end ? `, active ${start} → ${end}` : ""}`;

    case "remove_user":
      return `Removed a user${start ? ` (effective ${start})` : ""}`;

    case "add_company":
      return `Added a company to the subscription${
        start && end ? ` (${start} → ${end})` : ""
      }`;

    case "remove_company":
      return `Removed a company from the subscription${
        start ? ` (effective ${start})` : ""
      }`;

    case "renew_subscription":
      return `Subscription renewed${
        start && end ? ` for period ${start} → ${end}` : ""
      }`;

    case "cancel_subscription":
      return `Subscription cancelled${end ? ` (ends on ${end})` : ""}`;

    case "update_plan":
      return `Plan updated${start ? ` (effective ${start})` : ""}`;

    default:
      return "Subscription updated";
  }
};

const paperBorderSx = subscriptionCardSx;

const normalizeResponseData = (response) =>
  response?.data?.data || response?.data || {};

const normalizeSubscriptionContext = (payload) => {
  const raw = payload?.subscription || payload || {};
  const owner = raw.owner || payload?.user || null;
  const plan = raw.plan || null;

  return {
    user: { ...owner, organization_name: raw.organization?.name || "" },
    subscription: {
      ...raw,
      subId: raw.id || raw.subId || "",
      id: raw.id || raw.subId || "",
      planId: raw.plan_id || raw.planId || plan?.id || "",
      planName: plan?.name || raw.planName || raw.plan_id || "Unknown",
      nextBilling: raw.next_billing_date || raw.nextBilling || "",
      trialEnd: raw.trial_end || raw.trialEnd || "",
      currency: raw.currency || plan?.currency || "SGD",
      pausedUntil: raw.paused_until || raw.pausedUntil || null,
      trialResetHistory: raw.trial_reset_history || raw.trialResetHistory || 0,
      is_open_beta: raw.is_open_beta ?? raw.isOpenBeta ?? false,
      custom_discount_percent: raw.custom_discount_percent ?? null,
      owner_id: raw.owner_id || owner?.u_id || "",
      owner,
    },
  };
};

function formatDiscountPercentDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "0";
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

function trialReductionPresetDays(maxDays) {
  if (maxDays < 1) return [];
  const candidates = [1, 7, 14, 21, 30, maxDays];
  return [...new Set(candidates.filter((d) => d >= 1 && d <= maxDays))].sort(
    (a, b) => a - b
  );
}

const SubscriptionsConsole = () => {
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [completedAction, setCompletedAction] = useState(null);

  const requestSequence = useRef(0);
  useEffect(
    () => () => {
      requestSequence.current += 1;
    },
    []
  );

  const [plans, setPlans] = useState([]);

  const [search, setSearch] = useState("");
  const [context, setContext] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(true);
  const [appliedSearch, setAppliedSearch] = useState("");
  const searchInputRef = useRef(null);
  const changeSearchRef = useRef(null);
  const [snack, setSnack] = useState(null);

  const [discountValue, setDiscountValue] = useState("");

  const [offlineReferenceNo, setOfflineReferenceNo] = useState("");
  const [offlineNotes, setOfflineNotes] = useState("");
  const [offlineReason, setOfflineReason] = useState(
    "Customer made payment via non-HitPay method"
  );

  const [targetPlan, setTargetPlan] = useState("");

  const [newOwnerLocked, setNewOwnerLocked] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [payments, setPayments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reason, setReason] = useState("");

  const [trialReason, setTrialReason] = useState("Admin trial adjustment");
  const [customReductionDays, setCustomReductionDays] = useState(0);
  const [trialExtendTargetDate, setTrialExtendTargetDate] = useState("");
  const [trialReduceTargetDate, setTrialReduceTargetDate] = useState("");

  const { guardProps, runActionWithMiddleware } = useGuardRequest({
    policy: {
      applyCustomDiscount: true,
      migrateSubscriptionPlan: true,
      transferSubscriptionOwner: true,
      extendTrialSubscription: true,
      resetTrialSubscription: true,
      reduceTrialSubscription: true,
      settleOfflinePayment: true,
      manageSubscriptionSubUsers: true,
      prepareSubscriptionRenewal: true,
    },
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await adminSubscriptionServices.getPlans();
        setPlans(Array.isArray(res) ? res : []);
      } catch {
        setSnack({
          message: "Could not load plans. Changing plans is unavailable.",
          severity: "warning",
        });
      }
    };
    fetchPlans();
  }, []);

  const sub = context?.subscription;
  const user = context?.user;
  const compactSearch = Boolean(context) && !searchExpanded;
  useEffect(() => {
    if (searchExpanded) {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    } else {
      changeSearchRef.current?.focus();
    }
  }, [searchExpanded]);
  const discountUnchanged =
    sub?.custom_discount_percent != null &&
    parseDiscountInput(discountValue) === Number(sub.custom_discount_percent);

  useEffect(() => {
    setTrialExtendTargetDate("");
    setTrialReduceTargetDate("");
  }, [sub?.id, sub?.trialEnd]);

  useEffect(() => {
    setDiscountValue(
      sub?.custom_discount_percent == null
        ? ""
        : formatDiscountPercentDisplay(sub.custom_discount_percent)
    );
  }, [sub?.id, sub?.custom_discount_percent]);

  const planChangePreview = useMemo(() => {
    if (!sub) return "";
    const cur = plans.find((p) => p.id === sub.plan_id);
    const next = plans.find((p) => p.id === targetPlan);
    if (!cur || !next) return "";
    const discount = effectiveDiscount(sub);
    return {
      currentPlan: cur.name,
      nextPlan: next.name,
      discountLabel:
        discount.percent == null
          ? "Discount unavailable"
          : `Discount: ${discount.percent}%`,
      discountSource: discount.source,
    };
  }, [sub, targetPlan, plans]);

  const pushAudit = useCallback((actor, message) => {
    const at = new Date().toISOString().slice(0, 16).replace("T", " ");
    setAudit((prev) => [{ at, actor, message }, ...prev]);
  }, []);

  const applySnapshot = (payload, resetControls = false) => {
    const normalized = normalizeSubscriptionContext(payload);
    if (!normalized.subscription.id)
      throw new Error("Invalid subscription response");
    const snapshot = normalized.subscription;
    setContext(normalized);
    setRefreshedAt(new Date().toISOString());
    setPricingError("");
    setPayments(
      (snapshot.payments || []).map((payment) => ({
        at: payment.paid_at ? new Date(payment.paid_at).toLocaleString() : "—",
        outcome: payment.status || "unknown",
        reason: payment.payment_method || "—",
        amount_cents: payment.amount_cents,
        currency: payment.currency || snapshot.currency,
      }))
    );
    setAudit(
      [...(snapshot.change_logs || [])]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((change) => ({
          at: formatDate(change.created_at) || "—",
          status: change.status,
          message: getMessage(change, snapshot.currency),
        }))
    );
    setMembers(
      (snapshot.users || [])
        .filter((row) => row.user_auth?.user_type !== "admin")
        .sort((a, b) => (a.email || "").localeCompare(b.email || ""))
        .map((member) => ({
          id: member.u_id,
          email: member.email,
          type: formatUserType(member.user_auth?.user_type || "missing"),
          role: member.user_auth?.user_role || "missing",
        }))
    );
    if (resetControls) {
      setTargetPlan("");
      setSelectedMemberId(null);
      setNewOwnerLocked(null);
      setReason("");
      setTrialReason("Admin trial adjustment");
      setCustomReductionDays(0);
      setOfflineReferenceNo("");
      setOfflineNotes("");
    }
    return snapshot;
  };

  // Always reread the subscription that was changed, independent of the lookup draft.
  const refreshSubscription = async (id = sub?.id) => {
    if (!id) return null;
    const sequence = ++requestSequence.current;
    setRefreshing(true);
    try {
      const response =
        await adminSubscriptionServices.getSubscriptionDetail(id);
      if (sequence !== requestSequence.current) return null;
      const payload = normalizeResponseData(response);
      if ((payload.subscription || payload).id !== id)
        throw new Error("Subscription response did not match");
      return applySnapshot(payload);
    } catch {
      if (sequence === requestSequence.current) {
        setPricingError(
          "Could not reread this subscription. Any saved changes have not yet been verified. Refresh before making another change."
        );
      }
      return null;
    } finally {
      if (sequence === requestSequence.current) setRefreshing(false);
    }
  };

  const handleSearch = async () => {
    if (loading || updating || refreshing || guardProps.open) return;
    const q = search.trim();
    if (!q) {
      setSnack({ message: "Enter email / org name." });
      return;
    }
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q);
      const listRes = await adminSubscriptionServices.getSubscriptions({
        page: 1,
        limit: 1,
        owner_email: isEmail ? q : undefined,
        name: !isEmail ? q : undefined,
      });
      if (sequence !== requestSequence.current) return;
      const firstSub = normalizeResponseData(listRes)?.subscriptions?.[0];
      if (!firstSub?.id) throw new Error("no_match");
      const detailRes = await adminSubscriptionServices.getSubscriptionDetail(
        firstSub.id
      );
      if (sequence !== requestSequence.current) return;
      const payload = normalizeResponseData(detailRes);
      if ((payload.subscription || payload).id !== firstSub.id)
        throw new Error("Subscription response did not match");
      applySnapshot(payload, true);
      setAppliedSearch(q);
      setSearchExpanded(false);
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setContext(null);
      setPayments([]);
      setAudit([]);
      setSnack({
        message:
          error.message === "no_match"
            ? "No user or subscription found"
            : "Could not load subscription. Please retry.",
        severity: "error",
      });
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  };

  const getTrialActionErrorMessage = (error, fallback) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

  const handleExtendTrial = (additionalDays) => {
    const r = trialReason.trim();
    if (!r) {
      setSnack({ message: "Reason is required.", severity: "error" });
      return;
    }
    if (!sub?.id) return;

    runActionWithMiddleware({
      actionKey: "extendTrialSubscription",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Extend trial by {additionalDays} days for subscription {sub.id}.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const response = await adminSubscriptionServices.extendTrial(sub.id, {
            additional_days: additionalDays,
            reason: r,
          });
          pushAudit(
            "Admin",
            `Trial extended +${additionalDays} days (${response.data?.message || "ok"})`
          );
          setSnack({
            message: response.data?.message || "Trial extended successfully",
          });
          const saved = await refreshSubscription(sub.id);
          if (saved) setCompletedAction({ key: "trial" });
        } catch (error) {
          setSnack({
            message: getTrialActionErrorMessage(
              error,
              "Failed to extend trial"
            ),
            severity: "error",
          });
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const handleResetTrial = () => {
    const r = trialReason.trim();
    if (!r) {
      setSnack({ message: "Reason is required.", severity: "error" });
      return;
    }
    if (!sub?.id) return;

    runActionWithMiddleware({
      actionKey: "resetTrialSubscription",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Reset subscription {sub.id} to trial state.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const response = await adminSubscriptionServices.resetTrial(sub.id, {
            reason: r,
          });
          pushAudit("Admin", `Trial reset (${response.data?.message || "ok"})`);
          setSnack({
            message:
              response.data?.message ||
              "Subscription reset to trial successfully",
          });
          const saved = await refreshSubscription(sub.id);
          if (saved) setCompletedAction({ key: "trial" });
        } catch (error) {
          setSnack({
            message: getTrialActionErrorMessage(error, "Failed to reset trial"),
            severity: "error",
          });
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const maxTrialReduction = useMemo(
    () =>
      sub?.status === "trialing" && sub?.trialEnd
        ? maxTrialReductionDays(sub.trialEnd)
        : 0,
    [sub?.status, sub?.trialEnd]
  );

  const trialReductionPresets = useMemo(
    () => trialReductionPresetDays(maxTrialReduction),
    [maxTrialReduction]
  );

  const trialReduceDateBounds = useMemo(() => {
    if (sub?.status !== "trialing" || !sub?.trialEnd) {
      return {
        minStr: "",
        maxStr: "",
        minDate: null,
        maxDate: null,
        minLabel: "",
        maxLabel: "",
      };
    }
    const end = new Date(sub.trialEnd);
    if (Number.isNaN(end.getTime())) {
      return {
        minStr: "",
        maxStr: "",
        minDate: null,
        maxDate: null,
        minLabel: "",
        maxLabel: "",
      };
    }
    const maxR = maxTrialReductionDays(sub.trialEnd);
    if (maxR < 1) {
      return {
        minStr: "",
        maxStr: "",
        minDate: null,
        maxDate: null,
        minLabel: "",
        maxLabel: "",
      };
    }
    const minD = trialDate(shiftTrialDate(end, -maxR));
    const maxD = trialDate(shiftTrialDate(end, -1));
    if (!minD || !maxD) {
      return {
        minStr: "",
        maxStr: "",
        minDate: null,
        maxDate: null,
        minLabel: "",
        maxLabel: "",
      };
    }
    return {
      minStr: trialDateInput(minD),
      maxStr: trialDateInput(maxD),
      minDate: minD,
      maxDate: maxD,
      minLabel: formatTrialDate(minD),
      maxLabel: formatTrialDate(maxD),
    };
  }, [sub?.status, sub?.trialEnd]);

  const trialExtendDateBounds = useMemo(() => {
    if (sub?.status !== "trialing" || !sub?.trialEnd) {
      return { minStr: "", maxStr: "" };
    }
    const start = trialDate(sub.trialEnd);
    if (!start) return { minStr: "", maxStr: "" };
    const minD = shiftTrialDate(start, 1);
    const maxD = shiftTrialDate(start, 365);
    return {
      minStr: trialDateInput(minD),
      maxStr: trialDateInput(maxD),
    };
  }, [sub?.status, sub?.trialEnd]);

  const extendDaysForPicker = useMemo(() => {
    if (
      !trialExtendTargetDate ||
      sub?.status !== "trialing" ||
      !sub?.trialEnd
    ) {
      return { days: null, hint: null };
    }
    const target = parseTrialDateInput(trialExtendTargetDate);
    const from = trialDate(sub.trialEnd);
    if (!target || !from) return { days: null, hint: "Invalid date." };
    const days = trialDaysBetween(from, target);
    if (days < 1) {
      return {
        days: null,
        hint: "Choose a date after the current trial end (at least +1 day).",
      };
    }
    if (days > 365) {
      return {
        days: null,
        hint: "Maximum 365 additional days from current trial end.",
      };
    }
    return {
      days,
      hint: `Adds +${days} calendar day${days === 1 ? "" : "s"} to the current trial end.`,
    };
  }, [trialExtendTargetDate, sub?.trialEnd, sub?.status]);

  const reduceDaysForPicker = useMemo(() => {
    if (
      !trialReduceTargetDate ||
      sub?.status !== "trialing" ||
      !sub?.trialEnd
    ) {
      return { days: null, hint: null };
    }
    const target = parseTrialDateInput(trialReduceTargetDate);
    const trialEndDay = trialDate(sub.trialEnd);
    if (!target || !trialEndDay) return { days: null, hint: "Invalid date." };
    const maxR = maxTrialReductionDays(sub.trialEnd);
    const n = trialDaysBetween(target, trialEndDay);
    if (n < 1 || n > maxR) {
      return {
        days: null,
        hint:
          trialReduceDateBounds.minLabel && trialReduceDateBounds.maxLabel
            ? `Pick a date between ${trialReduceDateBounds.minLabel} and ${trialReduceDateBounds.maxLabel} (inclusive).`
            : "Pick a date within the allowed range.",
      };
    }
    return {
      days: n,
      hint: `Shortens trial by −${n} day${n === 1 ? "" : "s"} (new end matches selected date).`,
    };
  }, [
    trialReduceTargetDate,
    sub?.trialEnd,
    sub?.status,
    trialReduceDateBounds.minLabel,
    trialReduceDateBounds.maxLabel,
  ]);

  const handleReduceTrial = (reductionDays) => {
    const r = trialReason.trim();
    if (!r) {
      setSnack({ message: "Reason is required.", severity: "error" });
      return;
    }
    if (!sub?.id) return;
    const max = maxTrialReductionDays(sub.trialEnd);
    const n = Number(reductionDays);
    if (!Number.isInteger(n) || n < 1 || n > max) {
      setSnack({
        message: `Reduction must be between 1 and ${max} day(s) for this subscription.`,
        severity: "error",
      });
      return;
    }

    runActionWithMiddleware({
      actionKey: "reduceTrialSubscription",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Shorten trial by {n} day{n === 1 ? "" : "s"} for subscription {sub.id}
          (max {max} for current trial window).
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const response = await adminSubscriptionServices.reduceTrial(sub.id, {
            reduction_days: n,
            reason: r,
          });
          pushAudit(
            "Admin",
            `Trial shortened by ${n} day(s) (${response.data?.message || "ok"})`
          );
          setSnack({
            message: response.data?.message || "Trial reduced successfully",
          });
          const saved = await refreshSubscription(sub.id);
          if (saved) setCompletedAction({ key: "trial" });
        } catch (error) {
          setSnack({
            message: getTrialActionErrorMessage(
              error,
              "Failed to shorten trial"
            ),
            severity: "error",
          });
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const submitDiscount = async () => {
    const pct = parseDiscountInput(discountValue);
    if (
      pct == null ||
      discountUnchanged ||
      !sub?.id ||
      updating ||
      refreshing ||
      pricingError
    )
      return;
    await runActionWithMiddleware({
      actionKey: "applyCustomDiscount",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm {pct}% custom discount for {sub.id}. Current effective
          discount: {effectiveDiscount(sub).percent ?? "unknown"}% (
          {effectiveDiscount(sub).source}).
          This replaces the saved subscription discount, including any promo
          discount. Existing unpaid charges keep their recorded discount.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          await adminSubscriptionServices.updateCustomDiscount(sub.id, {
            custom_discount_percent: pct,
            reason: reason || "Admin applied discount",
          });
          const saved = await refreshSubscription(sub.id);
          if (!saved) {
            setSnack({
              message:
                "Discount saved, but pricing could not be refreshed. Verification is pending.",
              severity: "warning",
            });
          } else if (
            saved.custom_discount_percent == null ||
            Number(saved.custom_discount_percent) !== pct
          ) {
            setPricingError(
              "The saved discount readback does not match the requested value. Refresh and review before making another change."
            );
            setSnack({
              message:
                "The server readback does not match the requested discount.",
              severity: "warning",
            });
          } else {
            setCompletedAction({ key: "discount" });
            setSnack({
              message: `${pct}% custom discount confirmed by server readback. Review the pricing check below.`,
              severity: "success",
            });
          }
        } catch (error) {
          setSnack({ message: `Error: ${error.message}`, severity: "error" });
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const submitSettleOfflinePayment = async () => {
    const ref = offlineReferenceNo.trim();
    const r = offlineReason.trim();
    if (!ref) {
      setSnack({ message: "Reference number is required.", severity: "error" });
      return;
    }
    if (!r) {
      setSnack({ message: "Reason is required.", severity: "error" });
      return;
    }
    if (!sub?.id) return;

    await runActionWithMiddleware({
      actionKey: "settleOfflinePayment",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm that payment was received outside HitPay for subscription{" "}
          {sub.id}. All unpaid charges included in the next bill will be marked
          as paid, and an offline payment record will be saved.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const response = await adminSubscriptionServices.settleOfflinePayment(
            sub.id,
            {
              reference_no: ref,
              notes: offlineNotes.trim() || undefined,
              reason: r,
            }
          );
          pushAudit("Admin", `Offline payment settled (ref: ${ref})`);
          setSnack({ message: response.data.message });
          setOfflineReferenceNo("");
          setOfflineNotes("");
          setOfflineReason("");
          const saved = await refreshSubscription(sub.id);
          if (saved) setCompletedAction({ key: "offline" });
        } catch (error) {
          setSnack({
            message: getTrialActionErrorMessage(
              error,
              "Failed to settle offline payment"
            ),
            severity: "error",
          });
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const submitPlanMigration = async () => {
    if (
      !plans.some((plan) => plan.id === targetPlan) ||
      updating ||
      refreshing ||
      pricingError
    )
      return;
    await runActionWithMiddleware({
      actionKey: "migrateSubscriptionPlan",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm the change to {plans.find((p) => p.id === targetPlan).name}.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          // 调用 API 5.7: Renew 或 Recycle
          // 注意：API 5.7 用于处理 plan 变更
          const p = plans.find((x) => x.id === targetPlan);

          const response = await adminSubscriptionServices.updateSubscription({
            user_id: sub.owner_id, // 前端注意：传 subscription owner id
            subscription: {
              status: "active",
              plan_id: p.id, // 保持原 plan，仅改状态
              is_open_beta: sub.is_open_beta || false, // 建议显式传入
            },
            reason: "Admin plan migration",
          });

          pushAudit("Admin", `Plan migration → ${p?.name}`);
          setSnack({ message: response.data.message });

          const saved = await refreshSubscription(sub.id);
          if (saved) setCompletedAction({ key: "plan" });
        } catch (error) {
          setSnack({ message: `Error: ${error.message}`, severity: "error" });
        }
        setUpdating(false);
      },
    });
  };

  const runTransfer = async () => {
    await runActionWithMiddleware({
      actionKey: "transferSubscriptionOwner",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Confirm ownership transfer to {newOwnerLocked?.email || "target user"}
          .
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          // 调用 API 5.10: 转移 Owner
          const response = await adminSubscriptionServices.transferOwner(
            sub.id,
            {
              new_owner_id: newOwnerLocked.id,
              reason: reason || "Admin owner transfer",
            }
          );

          pushAudit("Admin", `Owner transfer → ${newOwnerLocked.email}`);
          setSnack({ message: response.data.message });
          const saved = await refreshSubscription(sub.id);
          if (saved) setCompletedAction({ key: "ownership" });
        } catch (error) {
          setSnack({ message: `Error: ${error.message}`, severity: "error" });
        }
        setUpdating(false);

        setSelectedMemberId(null);
        setNewOwnerLocked(null);
        setReason("");
      },
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        p: { xs: 2, lg: 3 },
        boxSizing: "border-box",
      }}
    >
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2.5 }}>
        <Avatar
          variant='rounded'
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <CreditCardOutlinedIcon />
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          <Typography component='h1' variant='h5' fontWeight={750}>
            Subscription Management
          </Typography>

          <Typography variant='body2' color='text.secondary'>
            Manage subscriptions, pricing, and billing details.
          </Typography>
        </Box>
      </Stack>

      <Paper
        component='form'
        elevation={0}
        sx={{
          ...subscriptionCardSx,
          flexShrink: 0,
          p: compactSearch ? 1.25 : { xs: 2, sm: 2.5 },
          mb: compactSearch ? 1.5 : 2.5,
        }}
        onSubmit={(event) => {
          event.preventDefault();
          handleSearch();
        }}
      >
        {compactSearch ? (
          <Stack direction='row' alignItems='center' gap={1.5}>
            <SearchIcon
              fontSize='small'
              sx={{ color: "text.secondary", flexShrink: 0 }}
            />
            <Typography
              variant='body2'
              color='text.secondary'
              noWrap
              title={appliedSearch}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Search: {appliedSearch}
            </Typography>
            <Button
              ref={changeSearchRef}
              type='button'
              size='small'
              onClick={() => setSearchExpanded(true)}
              disabled={loading || updating || refreshing || guardProps.open}
              aria-expanded={false}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Change search
            </Button>
          </Stack>
        ) : (
          <Stack id='subscription-search-fields' spacing={2}>
            <Box
              sx={{ display: { xs: context ? "none" : "block", sm: "block" } }}
            >
              <Typography variant='subtitle2' fontWeight={700}>
                Find a subscription
              </Typography>

              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.25 }}
              >
                Search by organization name or subscription owner's email.
              </Typography>
            </Box>

            <Stack
              direction='row'
              spacing={1.5}
              alignItems={{ sm: "flex-start" }}
            >
              <TextField
                inputRef={searchInputRef}
                fullWidth
                size='small'
                placeholder='Organization name or owner email'
                inputProps={{
                  "aria-label": "Organization name or owner email",
                }}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={loading || updating || refreshing || guardProps.open}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type='submit'
                variant='contained'
                startIcon={<SearchIcon />}
                disabled={loading || updating || refreshing || guardProps.open}
                sx={{
                  px: 3,
                  minHeight: 40,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  width: "auto",
                }}
              >
                {loading ? "Searching..." : "Search"}
              </Button>
            </Stack>
            {context && (
              <Button
                type='button'
                size='small'
                onClick={() => {
                  setSearch(appliedSearch);
                  setSearchExpanded(false);
                }}
                disabled={loading || updating || refreshing || guardProps.open}
                sx={{ alignSelf: "flex-end", textTransform: "none" }}
              >
                Keep current subscription
              </Button>
            )}
          </Stack>
        )}
      </Paper>

      {loading && <SubscriptionLoadingView paperBorderSx={paperBorderSx} />}

      {!context && !loading && (
        <SubscriptionEmptyView paperBorderSx={paperBorderSx} />
      )}

      {context && !loading && (
        <SubscriptionPricingPanel
          key={sub.id}
          subscription={sub}
          user={user}
          refreshedAt={refreshedAt}
          error={pricingError}
          loading={refreshing || updating || guardProps.open}
          onRefresh={() => refreshSubscription(sub.id)}
          payments={payments}
          audit={audit}
          completedAction={completedAction}
          actions={{
            plan: {
              sub,
              plans,
              targetPlan,
              setTargetPlan,
              planChangePreview,
              submitPlanMigration,
              updating,
              refreshing,
              pricingError,
            },
            discount: {
              sub,
              discountValue,
              setDiscountValue,
              discountUnchanged,
              submitDiscount,
              updating,
              refreshing,
              pricingError,
            },
            ownership: {
              user,
              members,
              selectedMemberId,
              setSelectedMemberId,
              setNewOwnerLocked,
              runTransfer,
            },
            offline: {
              sub,
              offlineReferenceNo,
              setOfflineReferenceNo,
              offlineNotes,
              setOfflineNotes,
              offlineReason,
              setOfflineReason,
              submitSettleOfflinePayment,
              updating,
            },
            subUsers: {
              subscriptionId: sub.id,
              status: sub.status,
              updating: updating || refreshing || guardProps.open,
              runActionWithMiddleware,
              setUpdating,
              onComplete: async (message) => {
                pushAudit("Admin", message);
                setSnack({ message, severity: "success" });
                await refreshSubscription(sub.id);
              },
              onError: (message) =>
                setSnack({ message, severity: "error" }),
            },
            renewCycle: {
              subscriptionId: sub.id,
              status: sub.status,
              nextBillingDate: sub.next_billing_date,
              updating: updating || refreshing || guardProps.open,
              runActionWithMiddleware,
              setUpdating,
              onComplete: async (message) => {
                pushAudit("Admin", message);
                setSnack({ message, severity: "success" });
                const saved = await refreshSubscription(sub.id);
                if (saved) setCompletedAction({ key: "renewCycle" });
              },
              onError: (message) =>
                setSnack({ message, severity: "error" }),
            },
            trial: {
              sub,
              updating,
              trialReason,
              setTrialReason,
              handleExtendTrial,
              handleResetTrial,
              trialExtendTargetDate,
              setTrialExtendTargetDate,
              trialExtendDateBounds,
              extendDaysForPicker,
              maxTrialReduction,
              trialReduceDateBounds,
              trialReduceTargetDate,
              setTrialReduceTargetDate,
              reduceDaysForPicker,
              handleReduceTrial,
              trialReductionPresets,
              customReductionDays,
              setCustomReductionDays,
            },
          }}
        />
      )}

      <ProcessingDialog loading={updating} />

      <SecurityGuard {...guardProps} />

      <Dialog
        open={Boolean(snack)}
        onClose={() => setSnack(null)}
        title={snack?.severity === "error" ? "Action failed" : "Message"}
        content={snack?.message || ""}
        confirmText='OK'
        cancelText='Close'
        onConfirm={() => setSnack(null)}
        maxWidth='xs'
      />
    </Box>
  );
};

export default SubscriptionsConsole;
